// @ts-check
// Peças comuns dos gráficos em SVG: medida do cartão, escala "bonita", cores por série, legenda e tooltip.
import { html } from 'htm/preact';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { Icon } from '../../ui/icon.js';
import { t } from '../../i18n/index.js';

/** Paleta categórica validada (ordem fixa, claro e escuro em dashboards.css). Depois da sexta série, "Outros" em cinza. */
export const SERIES_COLOR_COUNT = 6;
export const OTHER_SERIES_COLOR = 'var(--chart-series-other)';
export const COMPARISON_SERIES_COLOR = 'var(--chart-series-comparison)';
export const AXIS_FONT_SIZE = 11;
const AVERAGE_CHARACTER_WIDTH = 6.2;

/**
 * @param {number} index
 */
export function getSeriesColor(index) {
  return index < SERIES_COLOR_COUNT ? `var(--chart-series-${index + 1})` : OTHER_SERIES_COLOR;
}

/**
 * Cor escolhida no editor (nome de cor do tema) ou a primeira da paleta.
 * @param {string | null | undefined} colorName
 */
export function getSingleSeriesColor(colorName) {
  return colorName && colorName !== 'auto' ? `var(--chart-color-${colorName}, var(--chart-series-1))` : 'var(--chart-series-1)';
}

/**
 * Largura aproximada de um texto de 11 px, para margens e rótulos sem medir o DOM.
 * @param {string} text
 * @param {number} [fontSize]
 */
export function estimateTextWidth(text, fontSize = AXIS_FONT_SIZE) {
  return String(text).length * AVERAGE_CHARACTER_WIDTH * (fontSize / AXIS_FONT_SIZE);
}

/**
 * @param {string} text
 * @param {number} maxWidth
 * @param {number} [fontSize]
 */
export function truncateToWidth(text, maxWidth, fontSize = AXIS_FONT_SIZE) {
  const value = String(text ?? '');
  if (estimateTextWidth(value, fontSize) <= maxWidth) return value;
  const maxCharacters = Math.max(3, Math.floor(maxWidth / (AVERAGE_CHARACTER_WIDTH * (fontSize / AXIS_FONT_SIZE))) - 1);
  return `${value.slice(0, maxCharacters).trimEnd()}…`;
}

/**
 * Rótulos do eixo de categorias sem sobreposição nem corte. O primeiro alinha pelo começo e o último pelo fim (ficam
 * dentro do gráfico); os do meio ficam centrados no ponto, sem passar da borda. Um rótulo que encostaria no anterior
 * não aparece, menos o último, que entra no lugar do anterior (a data mais recente fica sempre à vista). Os
 * candidatos já vêm espaçados pelo passo; o primeiro alinhado pelo começo ocupa a largura inteira à direita do ponto,
 * e era ele que encostava no segundo.
 * @param {Array<{ key: string, text: string, x: number, isFirst: boolean, isLast: boolean }>} candidates da esquerda para a direita
 * @param {{ width: number, gap?: number, fontSize?: number }} options
 * @returns {Array<{ key: string, text: string, x: number, anchor: 'start' | 'middle' | 'end' }>}
 */
export function layoutCategoryLabels(candidates, { width, gap = 8, fontSize = AXIS_FONT_SIZE }) {
  /** @type {Array<{ key: string, text: string, x: number, anchor: 'start' | 'middle' | 'end', from: number, to: number }>} */
  const kept = [];
  for (const candidate of candidates) {
    const labelWidth = estimateTextWidth(candidate.text, fontSize);
    /** @type {'start' | 'middle' | 'end'} */
    const anchor = candidate.isFirst && !candidate.isLast ? 'start' : candidate.isLast && !candidate.isFirst ? 'end' : 'middle';
    const x = anchor === 'middle' ? Math.min(Math.max(candidate.x, labelWidth / 2), width - labelWidth / 2) : candidate.x;
    const from = anchor === 'start' ? x : anchor === 'end' ? x - labelWidth : x - labelWidth / 2;
    const placed = { key: candidate.key, text: candidate.text, x, anchor, from, to: from + labelWidth };
    if (candidate.isLast) {
      while (kept.length > 0 && kept[kept.length - 1].to + gap > placed.from) kept.pop();
    } else if (kept.length > 0 && kept[kept.length - 1].to + gap > placed.from) {
      continue;
    }
    kept.push(placed);
  }
  return kept.map(({ key, text, x, anchor }) => ({ key, text, x, anchor }));
}

/**
 * Domínio e marcas do eixo de valores com passos 1, 2, 2,5 ou 5 vezes potência de 10.
 * @param {number} minimum
 * @param {number} maximum
 * @param {number} [targetTickCount]
 * @param {{ integer?: boolean }} [options] contagens não têm marcas fracionárias (0,5 aluno)
 */
export function buildNiceScale(minimum, maximum, targetTickCount = 5, { integer = false } = {}) {
  const low = Math.min(0, minimum);
  const high = Math.max(maximum, low + 1);
  const rawStep = (high - low) / Math.max(1, targetTickCount);
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const niceStep = [1, 2, 2.5, 5, 10].map((factor) => factor * magnitude).find((candidate) => candidate >= rawStep) ?? 10 * magnitude;
  const step = integer ? Math.max(1, Math.ceil(niceStep)) : niceStep;
  const niceLow = Math.floor(low / step) * step;
  const niceHigh = Math.ceil(high / step) * step;
  /** @type {number[]} */
  const ticks = [];
  for (let value = niceLow; value <= niceHigh + step / 2; value += step) ticks.push(Math.round(value * 1e6) / 1e6);
  return { min: niceLow, max: niceHigh, ticks };
}

/**
 * @param {(number | null)[]} values
 */
export function areAllIntegers(values) {
  return values.every((value) => value === null || Number.isInteger(Number(value)));
}

/**
 * Tamanho do elemento acompanhando o cartão (ResizeObserver).
 * @returns {[{ current: HTMLDivElement | null }, { width: number, height: number }]}
 */
export function useElementSize() {
  const elementRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;
    const update = () => {
      const width = Math.floor(element.clientWidth);
      const height = Math.floor(element.clientHeight);
      setSize((current) => (current.width === width && current.height === height ? current : { width, height }));
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return [elementRef, size];
}

/**
 * Caminho de barra com cantos de 4 px só na ponta livre.
 * @param {number} x
 * @param {number} y
 * @param {number} width
 * @param {number} height
 * @param {'top' | 'right' | 'none'} roundedSide
 */
export function buildBarPath(x, y, width, height, roundedSide) {
  if (width <= 0 || height <= 0) return '';
  const radius = roundedSide === 'none' ? 0 : Math.min(4, roundedSide === 'top' ? width / 2 : height / 2, roundedSide === 'top' ? height : width);
  if (roundedSide === 'top') {
    return `M${x},${y + height}V${y + radius}Q${x},${y} ${x + radius},${y}H${x + width - radius}Q${x + width},${y} ${x + width},${y + radius}V${y + height}Z`;
  }
  if (roundedSide === 'right') {
    return `M${x},${y}H${x + width - radius}Q${x + width},${y} ${x + width},${y + radius}V${y + height - radius}Q${x + width},${y + height} ${x + width - radius},${y + height}H${x}Z`;
  }
  return `M${x},${y}H${x + width}V${y + height}H${x}Z`;
}

/**
 * @param {{ items: { key: string, label: string, color: string, dashed?: boolean }[] }} props
 */
export function ChartLegend({ items }) {
  if (items.length < 2) return null;
  return html`<div class="chart-legend">
    ${items.map((item) => html`<span class="chart-legend-item" key=${item.key}>
      <span class="chart-legend-swatch" data-dashed=${item.dashed ? 'true' : undefined} style=${{ '--swatch-color': item.color }}></span>
      <span class="chart-legend-label">${item.label}</span>
    </span>`)}
  </div>`;
}

// altura aproximada da dica com título, duas linhas e o rodapé do clique
const TOOLTIP_MINIMUM_TOP = 140;

/**
 * Tooltip HTML posicionado dentro do gráfico, virando para o lado com mais espaço. Num ponto que abre lista, o rodapé
 * diz como abrir (mouse ou teclado); fixado pelo toque, vira o botão "Ver registros".
 * @param {{ x: number, y: number, containerWidth: number, title: string, rows: { key: string, label: string, value: string, color?: string, dashed?: boolean }[],
 *   hint?: 'pointer' | 'keyboard' | null, action?: { href: string, onClick: (event: MouseEvent) => void } | null }} props
 */
export function ChartTooltip({ x, y, containerWidth, title, rows, hint = null, action = null }) {
  const placeLeft = x > containerWidth / 2;
  // perto do topo a dica (que sobe a partir do ponto) seria cortada pelo cartão: desce a partir dele
  const placeBelow = y < TOOLTIP_MINIMUM_TOP;
  return html`<div
    class="chart-tooltip"
    role="tooltip"
    data-below=${placeBelow ? 'true' : undefined}
    data-pinned=${action ? 'true' : undefined}
    data-chart-tooltip-pinned=${action ? 'true' : undefined}
    style=${{ top: `${placeBelow ? Math.max(0, y) + 12 : y}px`, left: placeLeft ? undefined : `${x + 12}px`, right: placeLeft ? `${containerWidth - x + 12}px` : undefined }}
  >
    <div class="chart-tooltip-title">${title}</div>
    ${rows.map((row) => html`<div class="chart-tooltip-row" key=${row.key}>
      ${row.color ? html`<span class="chart-legend-swatch" data-dashed=${row.dashed ? 'true' : undefined} style=${{ '--swatch-color': row.color }}></span>` : null}
      <span class="chart-tooltip-label">${row.label}</span>
      <span class="chart-tooltip-value">${row.value}</span>
    </div>`)}
    ${action
      ? html`<a class="chart-tooltip-action" href=${action.href} onClick=${action.onClick}>
          <span>${t('dashboardDrilldown.openButton')}</span><${Icon} name="IconArrowUpRight" size=${14} />
        </a>`
      : hint
        ? html`<div class="chart-tooltip-hint">
            <${Icon} name="IconArrowUpRight" size=${12} />
            <span>${t(hint === 'keyboard' ? 'dashboardDrilldown.tooltipHintKeyboard' : 'dashboardDrilldown.tooltipHint')}</span>
          </div>`
        : null}
  </div>`;
}
